import { useMemo, useRef, useState } from "react";
import {
  CheckCircle2,
  Eye,
  PenTool,
  RotateCcw,
  Trash2,
  Upload
} from "lucide-react";

import { ModalShell } from "@/components/ModalShell";
import { SignatureArtifact } from "@/components/SignatureArtifact";
import {
  SignatureStudioModal,
  type SignatureStudioMode
} from "@/components/SignatureStudioModal";

interface SignaturePadProps {
  disabled?: boolean;
  onChange: (value: string) => void;
  onSignerNameChange?: (value: string) => void;
  signedAt?: string;
  signerName: string;
  value: string;
}

type SignatureMethod = "upload" | "draw";

interface SignatureMethodCard {
  accentClassName: string;
  actionLabel: string;
  body: string;
  id: SignatureMethod;
  title: string;
}

const SIGNATURE_METHOD_CARDS: Record<SignatureMethod, SignatureMethodCard> = {
  upload: {
    id: "upload",
    title: "Upload Signature",
    body: "Bring in an image of your existing signature and review it instantly in the agreement.",
    actionLabel: "Open Upload",
    accentClassName:
      "from-[#f59e0b] via-[#f97316] to-[#ef4444] before:absolute before:-left-16 before:top-10 before:h-40 before:w-40 before:rounded-full before:border-[18px] before:border-white/30 before:content-['']"
  },
  draw: {
    id: "draw",
    title: "Draw Signature",
    body: "Use a full-screen workspace with a large canvas that works smoothly with mouse, trackpad, or touch.",
    actionLabel: "Open Draw Studio",
    accentClassName:
      "from-[#1fb6b2] via-[#10b981] to-[#059669] before:absolute before:-left-20 before:top-14 before:h-44 before:w-44 before:rounded-full before:border-[18px] before:border-white/30 before:content-[''] after:absolute after:bottom-14 after:right-10 after:h-20 after:w-20 after:rounded-full after:bg-white/25 after:content-['']"
  },
};

export function SignaturePad({
  disabled = false,
  onChange,
  onSignerNameChange: _onSignerNameChange,
  signedAt,
  signerName,
  value
}: SignaturePadProps) {
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [activeStudio, setActiveStudio] = useState<SignatureStudioMode | null>(null);
  const [lastMethod, setLastMethod] = useState<SignatureMethod | null>(null);
  const [statusMessage, setStatusMessage] = useState(
    "Choose Upload or Draw to create a polished signature for this agreement."
  );

  const hasSignature = Boolean(value);
  const displaySignerName = signerName.trim() || "Pending signer";

  const methodLabel = useMemo(() => {
    if (!lastMethod) {
      return hasSignature ? "Signature ready" : "Awaiting signature";
    }

    return lastMethod === "upload"
      ? "Uploaded signature ready"
      : "Drawn signature ready";
  }, [hasSignature, lastMethod]);

  function openUploadPicker() {
    if (disabled) {
      return;
    }

    fileInputRef.current?.click();
  }

  function openStudio(mode: SignatureStudioMode) {
    if (disabled) {
      return;
    }

    setActiveStudio(mode);
  }

  function reopenPreferredExperience() {
    if (disabled) {
      return;
    }

    if (lastMethod === "upload") {
      openUploadPicker();
      return;
    }

    setActiveStudio("draw");
  }

  function clearSignature() {
    onChange("");
    setLastMethod(null);
    setStatusMessage("Signature cleared. Choose Upload or Draw to add a fresh signature.");
  }

  function handleStudioSave(result: {
    dataUrl: string;
    method: SignatureStudioMode;
    signerName?: string;
  }) {
    onChange(result.dataUrl);
    setLastMethod("draw");
    setActiveStudio(null);
    setStatusMessage("Drawn signature saved. Review the preview below before submitting the agreement.");
  }

  function handleUpload(event: React.ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];

    if (!file) {
      return;
    }

    if (!file.type.startsWith("image/")) {
      setStatusMessage("Please upload an image file for the signature preview.");
      event.target.value = "";
      return;
    }

    const reader = new FileReader();

    reader.onload = () => {
      const result = typeof reader.result === "string" ? reader.result : "";

      if (!result) {
        setStatusMessage("We could not read that file. Please try a different image.");
        return;
      }

      onChange(result);
      setLastMethod("upload");
      setStatusMessage(
        `${file.name} uploaded successfully. Review the signature preview below before continuing.`
      );
    };

    reader.onerror = () => {
      setStatusMessage("We could not read that file. Please try a different image.");
    };

    reader.readAsDataURL(file);
    event.target.value = "";
  }

  return (
    <>
      <div className="signature-paper overflow-hidden rounded-[32px] border border-line/70 shadow-sm">
        <input
          accept="image/*"
          className="hidden"
          onChange={handleUpload}
          ref={fileInputRef}
          type="file"
        />

        <div className="flex flex-col gap-5 border-b border-line/70 px-5 py-6 md:px-6 md:py-7 xl:flex-row xl:items-start xl:justify-between">
          <div className="max-w-3xl">
            <p className="mt-3 text-sm leading-6 text-slate">
              {disabled
                ? "The submitted signature is locked for review. You can still open the preview below."
                : statusMessage}
            </p>

            <div className="mt-4 flex flex-wrap gap-2">
              <span className="inline-flex items-center gap-2 rounded-full bg-[#eef2ff] px-3 py-2 text-xs font-semibold uppercase tracking-[0.18em] text-[#1d4ed8]">
                <CheckCircle2 className="h-3.5 w-3.5" />
                {methodLabel}
              </span>
            </div>
          </div>

          <div className="flex flex-wrap gap-2">
            <button
              className="btn-secondary px-4 py-2.5"
              disabled={!hasSignature || disabled}
              onClick={reopenPreferredExperience}
              type="button"
            >
              <RotateCcw className="h-4 w-4" />
              Edit Signature
            </button>
            <button
              className="btn-ghost px-4 py-2.5"
              disabled={!hasSignature || disabled}
              onClick={clearSignature}
              type="button"
            >
              <Trash2 className="h-4 w-4" />
              Clear
            </button>
            <button
              className="btn-secondary px-4 py-2.5"
              disabled={!hasSignature}
              onClick={() => setIsPreviewOpen(true)}
              type="button"
            >
              <Eye className="h-4 w-4" />
              Show Preview
            </button>
          </div>
        </div>

        <div className="px-5 py-5 md:px-6 md:py-6">
          <div className="space-y-6">
            <div className="grid gap-4 md:grid-cols-2">
              <SignatureMethodOption
                card={SIGNATURE_METHOD_CARDS.upload}
                disabled={disabled}
                onClick={openUploadPicker}
              />
              <SignatureMethodOption
                card={SIGNATURE_METHOD_CARDS.draw}
                disabled={disabled}
                onClick={() => openStudio("draw")}
              />
            </div>

            <div className="rounded-[30px] border border-line/70 bg-white/88 p-4 shadow-sm md:p-5">
              <div className="mb-4 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                <div>
                  <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-slate/65">
                    Signature preview
                  </p>
                  <p className="mt-1 text-sm leading-6 text-slate">
                    The selected signature is always shown here after Upload or Draw.
                  </p>
                </div>
                <div className="rounded-full bg-cloud px-3 py-2 text-xs font-medium text-slate">
                  Works with mouse, touch, keyboard, or uploaded image
                </div>
              </div>

              {hasSignature ? (
                <SignatureArtifact
                  image={value}
                  signedAt={signedAt}
                  signerName={displaySignerName}
                />
              ) : (
                <div className="rounded-[28px] border border-dashed border-line/80 bg-[linear-gradient(180deg,rgba(255,255,255,0.92)_0%,rgba(248,250,252,0.92)_100%)] px-6 py-10 text-center">
                  <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-[20px] bg-[#eef2ff] text-[#1d4ed8]">
                    <PenTool className="h-6 w-6" />
                  </div>
                  <h4 className="mt-4 text-xl font-semibold text-ink">No signature added yet</h4>
                  <p className="mx-auto mt-3 max-w-2xl text-sm leading-6 text-slate">
                    Upload a signature image or open the drawing canvas. The preview will appear
                    here immediately after saving.
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <SignatureStudioModal
        initialName={signerName}
        mode={activeStudio}
        onClose={() => setActiveStudio(null)}
        onSave={handleStudioSave}
      />

      <ModalShell
        description="Review the signature block exactly as it will appear in the signed agreement."
        onClose={() => setIsPreviewOpen(false)}
        open={isPreviewOpen}
        title="Signature Preview"
        widthClassName="max-w-5xl"
      >
        <SignatureArtifact
          image={value}
          signedAt={signedAt}
          signerName={displaySignerName}
        />
      </ModalShell>
    </>
  );
}

function SignatureMethodOption({
  card,
  disabled,
  onClick
}: {
  card: SignatureMethodCard;
  disabled: boolean;
  onClick: () => void;
}) {
  const iconMap: Record<SignatureMethod, typeof Upload> = {
    upload: Upload,
    draw: PenTool
  };
  const Icon = iconMap[card.id];

  return (
    <button
      className={`group relative overflow-hidden rounded-[30px] bg-gradient-to-br px-5 py-5 text-left text-white shadow-[0_18px_48px_rgba(15,23,42,0.18)] transition duration-200 hover:-translate-y-1 disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:translate-y-0 ${card.accentClassName}`}
      disabled={disabled}
      onClick={onClick}
      type="button"
    >
      <div className="absolute inset-0 bg-[linear-gradient(180deg,rgba(255,255,255,0.04)_0%,rgba(15,23,42,0.12)_100%)]" />

      <div className="relative flex min-h-[256px] flex-col justify-between gap-10">
        <div className="flex items-start justify-between gap-4">
          <span className="flex h-14 w-14 items-center justify-center rounded-[20px] bg-white/90 text-ink shadow-sm">
            <Icon className="h-6 w-6" />
          </span>
          <span className="rounded-full bg-white/18 px-3 py-1.5 text-[11px] font-semibold uppercase tracking-[0.18em] text-white/90 backdrop-blur">
            {card.id}
          </span>
        </div>

        <div>
          <h4 className="text-2xl font-semibold tracking-tight text-white">{card.title}</h4>
          <p className="mt-3 max-w-[18rem] text-sm leading-6 text-white/88">{card.body}</p>
          <span className="mt-5 inline-flex rounded-[16px] bg-white px-4 py-2.5 text-sm font-semibold text-ink shadow-sm">
            {card.actionLabel}
          </span>
        </div>
      </div>
    </button>
  );
}
